import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { distinctUntilChanged } from 'rxjs';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { ImageCaptureMode } from '@main-application/inspections/models/rest-inspections-model.interface';
import {
  AreaPageElementCommentModel,
  AreaPageElementFileModel,
} from '@main-application/shared/template-editor-dialog/models/template-editor-model';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { IRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';

import { TemplateEditorDialogItemComponent } from '../template-editor-item.component';

@UntilDestroy()
@Component({
  selector: 'app-template-editor-constant-questions-item',
  templateUrl: './template-editor-constant-questions.component.html',
  styleUrls: ['../question/template-editor-question.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateEditorDialogConstantQuestionsComponent extends PuSubscribable implements OnInit {
  EIcon = EIcon;
  EColorPalette = EColorPalette;

  @Input() file: AreaPageElementFileModel;
  @Input() comment: AreaPageElementCommentModel;

  @Output() remove = new EventEmitter<AreaPageElementFileModel | AreaPageElementCommentModel>();
  @Output() questionItemChange = new EventEmitter<AreaPageElementFileModel | AreaPageElementCommentModel>();

  form: FormGroup;

  fileOptions: IRadioButtonOption<ImageCaptureMode>[] = [
    { value: ImageCaptureMode.PhotoAndGallery, label: 'photos (camera & gallery)' },
    { value: ImageCaptureMode.PhotoOnly, label: 'photos (camera only)' },
    { value: ImageCaptureMode.AnyFile, label: 'photos & documents' },
  ];

  selectedFileOptionLabel: string;
  fileSectionExpanded = false;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private templateEditorDialogItemComponent: TemplateEditorDialogItemComponent
  ) {
    super();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      fileOption: this.file.imageCaptureMode ?? ImageCaptureMode.PhotoAndGallery,
    });

    const option = this.fileOptions.find(o => o.value === this.form.get('fileOption')?.value);
    this.selectedFileOptionLabel = option ? option.label : 'files';

    this.form
      .get('fileOption')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe(value => this.updateFileOption(value))
      .untilDestroyed(this);

    this.templateEditorDialogItemComponent.expandQuestion$
      .pipe(distinctUntilChanged())
      .subscribe(expandedId => {
        if (expandedId !== 'file') {
          this.fileSectionExpanded = false;
          this.cdr.markForCheck();
        }
      })
      .untilDestroyed(this);
  }

  private updateFileOption(value: ImageCaptureMode) {
    const option = this.fileOptions.find(o => o.value === value);
    this.selectedFileOptionLabel = option ? option.label : 'files';
    this.questionItemChange.emit({ ...this.file, imageCaptureMode: value });
  }

  toggleSetting(id: 'file') {
    if (id === 'file') {
      this.fileSectionExpanded = !this.fileSectionExpanded;
      if (this.fileSectionExpanded) {
        this.templateEditorDialogItemComponent.expandQuestion$.next(id);
      }
    }
  }

  expandSetting(id: 'file'): void {
    if (id === 'file') {
      if (!this.fileSectionExpanded) {
        this.fileSectionExpanded = true;

        if (this.file?.visible === false) {
          this.file.visible = true;
          this.remove.emit({ ...this.file, visible: this.file.visible });
        }

        this.templateEditorDialogItemComponent.expandQuestion$.next(id);
        this.cdr.markForCheck();
      }
    }
  }

  toggleRemove(id: 'file' | 'comment') {
    if (id === 'file') {
      const current = this.file?.visible === false ? false : true;
      this.file.visible = !current;
      if (!this.file.visible) this.fileSectionExpanded = false;
      this.remove.emit({ ...this.file, visible: this.file.visible });
    }

    if (id === 'comment') {
      const current = this.comment?.visible === false ? false : true;
      this.comment.visible = !current;
      this.remove.emit({ ...this.comment, visible: this.comment.visible });
    }
  }
}
